import { memo, useCallback, useRef, useState } from "react";
import { FaVideo } from "react-icons/fa6";
import CertificateLink from "../../components/CertificateLink";
import MediaPreviewModal from "../../components/MediaPreviewModal";
import { noImgShapes } from "../../lib/noImgShapes";
import { formatPrice, formatCarat } from "../../lib/formatters";
import Loader from "../../components/Loader";
import DiamondGridSkeleton from "./DiamondGridSkeleton";

function buildTooltip(item) {
    const parts = [];
    parts.push(item?.CARAT ? formatCarat(item.CARAT) : "");
    parts.push(item?.SHAPE ?? "");
    if (item?.FANCY_COLOR) {
        parts.push(item.FANCY_INTENSITY, item.FANCY_OVERTONE, item.FANCY_COLOR);
    } else {
        parts.push(item?.COLOR ?? "");
    }
    parts.push(item?.CLARITY, item?.CUT, item?.POLISH, item?.SYMMETRY, item?.FLUORESCENCE, item?.LAB);
    return parts.filter(Boolean).join(" ");
}

function DiamondGrid({ naturalData, loading, from, onScrollEnd, moreLoading, handleViewDiamond }) {
    const [showMedia, setShowMedia] = useState(false);
    const [isMediaLoading, setIsMediaLoading] = useState(true);
    const [selectedRow, setSelectedRow] = useState(null);
    const [mediaType, setMediaType] = useState("video");

    // Throttle scroll-end to once per 400ms.
    const lastScrollEndRef = useRef(0);
    const handleScroll = useCallback(
        (e) => {
            const { scrollTop, scrollHeight, clientHeight } = e.target;
            if (scrollHeight - scrollTop > clientHeight + 50) return;
            const now = Date.now();
            if (now - lastScrollEndRef.current < 400) return;
            lastScrollEndRef.current = now;
            onScrollEnd?.();
        },
        [onScrollEnd]
    );

    return (
        <>
            <div className="flex flex-wrap pt-2">
                {loading ? (
                    <div className="w-full">
                        <DiamondGridSkeleton count={8} />
                    </div>
                ) : (
                    <div
                        className="w-full grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 pt-2 custom-scrollbar"
                        style={{ maxHeight: 800, overflowY: "auto" }}
                        onScroll={handleScroll}
                    >
                        {(naturalData || []).map((item) => (
                            <div
                                className="gridcard"
                                key={item?.STOCK_ID}
                            >
                                <div className="bg-gray-100">
                                    <img
                                        loading="lazy"
                                        className="cursor-pointer"
                                        onClick={() => handleViewDiamond(item)}
                                        src={item?.IMAGE || item?.sample}
                                        alt={item?.SHAPE}
                                        onError={(e) => {
                                            e.currentTarget.src =
                                                item?.sample || noImgShapes.noimage;
                                        }}
                                    />
                                </div>
                                <div className="px-4 mt-2 mb-2 flex justify-between items-center">
                                    <CertificateLink
                                        Certificate_link={item?.CERTIFICATE_LINK}
                                        lab={item?.LAB}
                                        certificate={item?.CERTIFICATE_NO}
                                        hideCertiNumber
                                        className="text-xs text-wahchan-primary"
                                    />
                                    {item?.VIDEO && (
                                        <FaVideo
                                            className="text-wahchan-primary cursor-pointer"
                                            onClick={() => {
                                                // Guard against re-opening same video while modal
                                                // already showing it (would refire loader stuck on cached iframe).
                                                if (
                                                    showMedia &&
                                                    selectedRow?.STOCK_ID === item?.STOCK_ID &&
                                                    mediaType === "video"
                                                ) {
                                                    return;
                                                }
                                                setIsMediaLoading(true);
                                                setShowMedia(true);
                                                setSelectedRow(item);
                                                setMediaType("video");
                                            }}
                                        />
                                    )}
                                </div>
                                <div
                                    className="px-4 truncate"
                                    title={buildTooltip(item)}
                                >
                                    <span className="text-gray-900 font-bold capitalize text-base">
                                        {[
                                            formatCarat(item?.CARAT),
                                            item?.SHAPE,
                                            ...(item?.FANCY_COLOR
                                                ? [
                                                      item.FANCY_INTENSITY,
                                                      item.FANCY_OVERTONE,
                                                      item.FANCY_COLOR,
                                                  ]
                                                : [item?.COLOR]),
                                            item?.CLARITY,
                                            item?.CUT,
                                            item?.POLISH,
                                            item?.SYMMETRY,
                                            item?.FLUORESCENCE,
                                        ]
                                            .filter(Boolean)
                                            .map((part, i) => (
                                                <span key={i} className="pr-2">
                                                    {part}
                                                </span>
                                            ))}
                                    </span>
                                    <hr className="mx-3 my-2" />
                                </div>
                                <div className="px-4 text-center mt-auto">
                                    <div className="text-xl font-extrabold text-wahchan-primary">
                                        <span
                                            title={`/CT ${formatPrice(
                                                item?.PRICE_PER_CTS,
                                                item?.CURRENCY
                                            )}`}
                                        >
                                            {formatPrice(
                                                item?.TOTAL_PRICE,
                                                item?.CURRENCY
                                            )}
                                        </span>
                                        <hr className="mx-3 mt-1 my-2" />
                                    </div>
                                    <div className="px-2 mb-3">
                                        <button
                                            className="btn-outline w-full text-sm"
                                            type="button"
                                            onClick={() => handleViewDiamond(item)}
                                        >
                                            View Diamond
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                {from === "result" && moreLoading && (
                    <div className="w-full">
                        <Loader message="Loading more Diamonds..." />
                    </div>
                )}
            </div>
            <MediaPreviewModal
                show={showMedia}
                onClose={() => setShowMedia(false)}
                mediaType={mediaType}
                item={selectedRow}
                isMediaLoading={isMediaLoading}
                setIsMediaLoading={setIsMediaLoading}
            />
        </>
    );
}

export default memo(DiamondGrid);
